import { useCallback, useMemo, useState } from 'react'
import Map, { Layer, MapLayerMouseEvent, Source } from 'react-map-gl/maplibre'

import layers, { EmptyLayer, LayerT } from './layers'
import { FeaturesT } from './utils/types'
import { MapPopover } from './Popover'
import { MAP_STYLES } from 'components/Dashboards/MapHeatmap/utils'
import { Box } from '@chakra-ui/react'
import { MapLayersColours } from 'modules/BioriskMpox/common/GraphColors'

const MapContainer = ({
  selectedLayer,
  mapRef,
  features,
  selectCountry,
  disease,
  mapLayers,
  legendType,
}: {
  selectedLayer: LayerT
  mapRef: any
  features: FeaturesT
  selectCountry: (country: string) => void
  disease?: string
  mapLayers?: string[]
  legendType?: string
}) => {
  const [hoverInfo, setHoverInfo] = useState<any>(null)

  const onHover = useCallback((event: any) => {
    const {
      features,
      point: { x, y },
      lngLat: { lng, lat },
    } = event
    const hoveredFeature = features && features[0]

    if (hoveredFeature?.layer?.id?.startsWith('data-countries')) {
      setHoverInfo(
        hoveredFeature && {
          feature: hoveredFeature,
          properties: hoveredFeature?.properties,
          x,
          y,
          lng,
          lat,
          isClade: true,
        }
      )
      return
    }

    const properties = JSON.parse(
      hoveredFeature?.properties?.properties || '{}'
    )

    // prettier-ignore
    setHoverInfo(hoveredFeature && {feature: hoveredFeature, properties, x, y, lng, lat});
  }, [])

  function getLayer(layer: LayerT) {
    if (layer && layer === selectedLayer) return features[layer]
    if (layer === '' || (selectedLayer !== layer && !!selectedLayer)) {
      return EmptyLayer
    }
    return features?.[layer] ?? EmptyLayer
  }

  function handleClick(event: MapLayerMouseEvent) {
    const countryId = event.features?.[0]?.properties?.id
    selectCountry(countryId)
  }
  function handleDotClick(countryId: string) {
    selectCountry(countryId)
  }

  const layersFeatures =
    mapLayers?.flatMap((layerName) => getLayer(layerName as any).features) ?? []
  const highestCaseNumber = layersFeatures
    .map((item) => item.properties.value)
    .sort((a, b) => b - a)[0]

  const interactiveLayerIds = mapLayers?.map(
    (layerName) => `data-${layerName.replace(/\s+/g, '-')}`
  ) ?? ['data-suspected', 'data-notSpecified', 'data-confirmed']

  const map = useMemo(() => {
    return (
      <Map
        initialViewState={{
          longitude: 0,
          latitude: 10,
          zoom: 0,
        }}
        renderWorldCopies={false}
        preserveDrawingBuffer={true}
        ref={mapRef}
        id='myMap'
        interactiveLayerIds={[
          'data-clade-selected',
          'data-countries',
          'data-clade',
          ...interactiveLayerIds,
        ]}
        onMouseMove={onHover}
        mapStyle={MAP_STYLES['gray-labels']}
        onClick={handleClick}
      >
        {!!features.clade && (
          <Source
            id='data-clade-selected'
            data={getLayer('clade')}
            type='geojson'
          >
            <Layer {...layers.CountryBorders} />
          </Source>
        )}
        {!!features.clade && (
          <Source id='data-clade' data={features.clade} type='geojson'>
            <Layer {...layers.CountryBorders} />
          </Source>
        )}
        {!!features.countries && (
          <Source id='data-countries' data={features.countries} type='geojson'>
            <Layer {...layers.CountryBorders} />
          </Source>
        )}
        {mapLayers?.map((layerName, index) => {
          if (layerName === 'clade' || layerName === 'countries') return null
          const data = getLayer(layerName as any)
          const id = `data-${layerName.replace(/\s+/g, '-')}`
          if (!data.features.length) return null
          return (
            <Source id={id} data={data} type={'geojson'} key={id}>
              <Layer
                {...layers.CircleConfirmed(
                  highestCaseNumber,
                  id,
                  MapLayersColours.specific[layerName] ||
                    MapLayersColours.extra[index],
                  mapLayers.length === 1
                )}
              />
            </Source>
          )
        })}
      </Map>
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [features, mapRef, highestCaseNumber, selectedLayer])

  return (
    <Box
      position={'relative'}
      height={'full'}
      width={'full'}
      borderRadius={'sm'}
      overflow={'visible'}
    >
      {map}
      {hoverInfo && (
        <MapPopover
          hoverInfo={hoverInfo}
          onClick={handleDotClick}
          legendType={legendType}
          mapWidth={mapRef.current?.getCanvasContainer().offsetWidth}
        />
      )}
    </Box>
  )
}

export default MapContainer
